// The minimap's callbacks, the camera footprint it draws, its pings, and the
// event log that shares the ping push. Extracted from App.tsx (ROADMAP 7.14);
// the hook order is unchanged.

import type { MinimapPoint } from '@aod/ui';
import { useCallback, useMemo } from 'react';
import { axialToWorld } from '../render/HexGrid.js';
import { beginCameraGrab, cancelCameraGrab, dragCameraTo } from '../render/camera/cameraInput.js';
import { useCameraView } from '../render/camera/cameraView.js';
import type { Vec3 } from '../render/camera/flight.js';
import { useSettings } from '../settings/useSettings.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';
import { visibleInLog } from '../ui/RightPanel/milestoneEventText.js';
import { cityPositions, eventToPingTarget, useMinimapPings } from '../ui/minimapPings.js';

export function useMinimapWiring(
  sim: SimWorld,
  localPlayerIndex: number,
  flyCameraTo: (position: Vec3) => void,
) {
  const handleMinimapClick = useCallback(
    (q: number, r: number) => {
      const [wx, , wz] = axialToWorld(q, r);
      flyCameraTo([wx, 0, wz]);
    },
    [flyCameraTo],
  );

  // Primary drag on the minimap moves the camera (grab: the ground point under the
  // cursor stays under it). Points arrive in the minimap's unit space, which is the
  // board's world (x, z) — HEX_SIZE is 1 and the axes match axialToWorld.
  const handleMinimapCameraDrag = useCallback((from: MinimapPoint, to: MinimapPoint) => {
    beginCameraGrab([from.x, from.y]);
    dragCameraTo([to.x, to.y]);
  }, []);

  // The drag is over: drop the grab so the next drag starts a fresh one.
  const handleMinimapCameraDragEnd = useCallback(() => {
    cancelCameraGrab();
  }, []);

  // The 3D camera's ground footprint, published from inside the Canvas at ~15 Hz
  // (change-only) in board coordinates — the minimap's unit space.
  const cameraPolygon = useCameraView((st) => st.polygon);
  const cameraPolygonPoints = useMemo(
    () => cameraPolygon?.map(([x, z]) => ({ x, y: z })) ?? null,
    [cameraPolygon],
  );
  // The ground point the camera looks at (the orbit target). The live region
  // announces this, not the footprint's centre: a tilted camera's footprint is
  // a long wedge, so its centroid is nowhere near the camera's real position.
  const cameraFocus = useCameraView((st) => st.focus);

  // Minimap pings (M3): rings on visible tiles where events happen, plus a
  // direct push from event-log clicks. particleEffects off = static rings
  // (prefers-reduced-motion is handled inside the minimap canvas).
  const particleEffects = useSettings((s) => s.particleEffects);
  const minimapViewBox = useSettings((s) => s.minimapViewBox);
  const setMinimapViewBox = useSettings((s) => s.setMinimapViewBox);
  const { pings, pushPing } = useMinimapPings(
    sim.events,
    sim.snapshot,
    sim.minimapSnapshot,
    localPlayerIndex,
  );

  // Event-log click: fly to the event's position and ping it there too.
  // The log is the local player's news: other players' small milestones stay out.
  const logEvents = useMemo(
    () => sim.events.filter((e) => visibleInLog(e, localPlayerIndex)),
    [sim.events, localPlayerIndex],
  );
  const handleEventClick = useCallback(
    (event: { id: number; type: string; payload?: Record<string, unknown> }) => {
      const target = eventToPingTarget(event, cityPositions(sim.snapshot));
      if (!target) return;
      const [wx, , wz] = axialToWorld(target.q, target.r);
      flyCameraTo([wx, 0, wz]);
      pushPing(target, 'info');
    },
    [sim.snapshot, flyCameraTo, pushPing],
  );

  return {
    handleMinimapClick,
    handleMinimapCameraDrag,
    handleMinimapCameraDragEnd,
    cameraPolygonPoints,
    cameraFocus,
    particleEffects,
    minimapViewBox,
    setMinimapViewBox,
    pings,
    logEvents,
    handleEventClick,
  };
}
